<template>
  <div class="rmr-page-shell auto-email-report-prog-page">
    <!-- Header Hero Banner (Exact match with Image 2 / CtcAssistantHero style) -->
    <rmr-page-hero
      class="auto-email-report-prog__hero"
      title="Rapport"
      title-accent="Mail Auto"
      subtitle="Gestion des destinataires, options d'envoi et suivi des métriques GSS"
      :icon="icons.mdiEmailSync"
    >
      <template #actions>
        <!-- Primary Navigation: Segmented Control on dark hero (Image 2 style) -->
        <rmr-segmented-control
          v-model="activeMainTab"
          :items="mainTabs"
          variant="on-dark"
          size="md"
        />
      </template>
    </rmr-page-hero>

    <!-- Main Content with Overlap on Hero (Exact match with Image 3 / Revenues style) -->
    <section class="rmr-page-shell__content auto-email-report-prog__content">
      <!-- 1. TAB: CONFIGURATION & ENVOIS -->
      <div v-show="activeMainTab === 'config'" class="auto-email-report-prog__tab-pane">
        <actors-perimeter embedded />
      </div>

      <!-- 2. TAB: DONNÉES -->
      <div v-show="activeMainTab === 'donnees'" class="auto-email-report-prog__tab-pane">
        <div class="rmr-table-shell auto-email-report-prog__data-shell">
          <!-- Secondary Navigation: RmrTabs at top of white card (Image 3 style) -->
          <div class="auto-email-report-prog__secondary-tabs">
            <rmr-tabs
              v-model="activeDatasetIndex"
              :tabs="datasetTabs"
              show-arrows
            />
          </div>

          <!-- Dataset View for selected secondary tab (with :key to prevent stale responses) -->
          <div class="auto-email-report-prog__dataset-body">
            <gss-dataset-view
              :key="currentDatasetName"
              :dataset-name="currentDatasetName"
              embedded
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  mdiEmailSync,
  mdiCogOutline,
  mdiDatabaseSyncOutline,
  mdiChartLine,
  mdiTarget,
  mdiFinance,
  mdiCashPlus,
  mdiAccountMultiplePlus,
  mdiHistory,
} from "@mdi/js";

import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import RmrTabs from "@/components/ui/layout/RmrTabs.vue";
import ActorsPerimeter from "./ActorsPerimeter.vue";
import GssDatasetView from "./GssDatasetView.vue";
import { GSS_DATASETS } from "@/config/gssDatasets";
import { saveRmraView } from "@/utils/rmr-analytics";

const MAIN_TABS = [
  {
    value: "config",
    label: "Configuration & Envois",
    icon: mdiCogOutline,
    title: "Gestion des destinataires, périmètres & options d'envoi automatique",
  },
  {
    value: "donnees",
    label: "Données",
    icon: mdiDatabaseSyncOutline,
    title: "Consultation et surcharge des métriques GSS",
  },
];

const DATASET_ICONS = {
  obj: mdiTarget,
  generale: mdiChartLine,
  generale_n1: mdiHistory,
  revenus_supp: mdiCashPlus,
  acquisition: mdiAccountMultiplePlus,
  finance: mdiFinance,
};

const resolveDatasetIndex = (val, datasets) => {
  if (!val) return 0;
  const target = String(val).toLowerCase().trim();
  const idx = datasets.findIndex(
    (ds) =>
      ds.name?.toLowerCase() === target ||
      ds.id?.toLowerCase() === target ||
      ds.path?.toLowerCase() === target ||
      ds.path?.toLowerCase() === target.replace(/_/g, "-") ||
      ds.name?.toLowerCase() === target.replace(/-/g, "_")
  );
  return idx >= 0 ? idx : 0;
};

export default {
  name: "RapportMailAutoProg",
  components: {
    RmrPageHero,
    RmrSegmentedControl,
    RmrTabs,
    ActorsPerimeter,
    GssDatasetView,
  },
  data() {
    // Parse query params on load
    const queryTab = this.$route.query.tab;
    const initialMainTab = queryTab === "donnees" ? "donnees" : "config";
    const initialDatasetIndex = resolveDatasetIndex(this.$route.query.dataset, GSS_DATASETS);

    return {
      icons: {
        mdiEmailSync,
        mdiCogOutline,
        mdiDatabaseSyncOutline,
      },
      mainTabs: MAIN_TABS,
      activeMainTab: initialMainTab,
      activeDatasetIndex: initialDatasetIndex,
      datasets: GSS_DATASETS,
      isNavigatingFromRoute: false,
    };
  },
  computed: {
    datasetTabs() {
      return this.datasets.map((ds) => ({
        key: ds.name,
        label: ds.label,
        icon: DATASET_ICONS[ds.name] || mdiChartLine,
      }));
    },
    currentDatasetName() {
      if (
        this.activeDatasetIndex >= 0 &&
        this.activeDatasetIndex < this.datasets.length
      ) {
        return this.datasets[this.activeDatasetIndex].name;
      }
      return this.datasets[0].name;
    },
  },
  watch: {
    activeMainTab(newTab) {
      if (!this.isNavigatingFromRoute && this.$route.query.tab !== newTab) {
        this.syncUrlParams({ tab: newTab });
      }
    },
    activeDatasetIndex(newIdx) {
      if (!this.isNavigatingFromRoute && this.activeMainTab === "donnees") {
        const dsName = this.datasets[newIdx]?.name;
        if (this.$route.query.dataset !== dsName) {
          this.syncUrlParams({ tab: "donnees", dataset: dsName });
        }
      }
    },
    "$route.query": {
      handler(newQuery) {
        this.isNavigatingFromRoute = true;
        // Sync main tab (fallback to 'config' if omitted or on browser back button)
        const targetTab = newQuery.tab === "donnees" ? "donnees" : "config";
        if (this.activeMainTab !== targetTab) {
          this.activeMainTab = targetTab;
        }

        // Sync dataset if in 'donnees' tab
        if (targetTab === "donnees") {
          const resolvedIdx = resolveDatasetIndex(newQuery.dataset, this.datasets);
          if (this.activeDatasetIndex !== resolvedIdx) {
            this.activeDatasetIndex = resolvedIdx;
          }
        }

        this.$nextTick(() => {
          this.isNavigatingFromRoute = false;
        });
      },
      deep: true,
    },
  },
  mounted() {
    saveRmraView(this.$route);
  },
  methods: {
    syncUrlParams({ tab, dataset }) {
      const nextQuery = { ...this.$route.query };

      if (tab) {
        nextQuery.tab = tab;
      }
      if (tab === "donnees") {
        const dsName = dataset || this.currentDatasetName;
        if (dsName) {
          nextQuery.dataset = dsName;
        }
      } else {
        delete nextQuery.dataset;
      }

      // Avoid redundant navigation errors
      if (
        this.$route.query.tab === nextQuery.tab &&
        this.$route.query.dataset === nextQuery.dataset
      ) {
        return;
      }

      this.$router
        .replace({
          path: this.$route.path,
          query: nextQuery,
        })
        .catch((err) => {
          if (err?.name !== "NavigationDuplicated") {
            console.error("Erreur de navigation :", err);
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.auto-email-report-prog-page {
  width: 100%;
}

.auto-email-report-prog__hero {
  padding-bottom: 40px !important;
}

.auto-email-report-prog__content {
  margin-top: -16px !important;
  padding-bottom: $rmr-space-xl;
  position: relative;
  z-index: 5;
}

.auto-email-report-prog__data-shell {
  background: $rmr-surface-0;
  border-radius: $rmr-radius-xl;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border: 1px solid $rmr-border;
}

.auto-email-report-prog__secondary-tabs {
  background: $rmr-surface-0;
  border-bottom: 1px solid $rmr-border;
  padding: 0 8px;
}

.auto-email-report-prog__dataset-body {
  background: #ffffff;
}
</style>
